<template>
  <header
    class="jps-header jps-header-fixed"
    :class="{
      'has-shadow': hasShadow,
    }"
    id="globalHeader"
  >
    <div class="jps-header-inner">
      <div class="jps-header-main">
        <BaseLink to="/" class="jps-header-main-logo">
          <BaseIcon
            name="jps/logo-horizontal-4c"
            alt="ジャパンサーチ"
            size="size-none"
            class="jps-header-main-logo-svg"
          />
        </BaseLink>

        <ul class="jps-header-main-nav">
          <li>
            <BaseLink
              to="/"
              class="jps-header-text-with-icon-link"
            >
              <BaseIcon name="mdi/home-outline" />
              <span>{{ lss("ホーム", "Home")}}</span>
            </BaseLink>
          </li>
          <li>
            <BaseLink
              to="/gallery"
              class="jps-header-text-with-icon-link"
            >
              <BaseIcon name="mdi/image-multiple-outline" />
              <span>{{ lss("ギャラリー", "Gallery")}}</span>
            </BaseLink>
          </li>
        </ul>
      </div>

      <nav class="jps-header-nav">
        <BaseLink to="/signin" class="jps-header-text-with-icon-link jps-header-management-item" v-if="userStore.isSigninOn && !userStore.isLogin">
          <BaseIcon name="mdi/login" alt="ログイン" />
        </BaseLink>

        <!-- 機関メニュー -->
        <BaseDropdown
          v-if="userStore.isLogin"
          class="jps-header-dropdown jps-header-management-item"
        >
          <template #trigger>
            <span class="jps-header-text-with-icon-link">
              <template v-if="!userStore.isOrg">
                <BaseIcon name="mdi/account-circle" />
                <span class="is-hidden-mobile">{{ userStore.getLoginUserId }}</span>
              </template>

              <template v-else>
                <BaseIcon name="mdi/bank" />
                {{ userStore.getLoginUserId }}@
                <UiOrgSpan :id="userStore.getOrgId"/>
              </template>
            </span>
          </template>

          <div
            class="jps-header-dropdown-body"
          >
            <span
              v-if="userStore.isOrg"
              class="dropdown-body-supplement"
            >{{
              led(userRoleDef, userStore.userRole)
            }}</span>

            <ul
              class="jps-header-dropdown-list"
            >
              <li v-if="userStore.isOrg">
                <ul>
                  <li>
                    <BaseDropdownItem>
                      <BaseLink
                        class="jps-header-dropdown-link"
                        to="/management"
                      >
                        <span class="text">管理トップ</span>
                      </BaseLink>
                    </BaseDropdownItem>
                  </li>
                  <li>
                    <BaseDropdownItem>
                      <BaseLink
                        class="jps-header-dropdown-link"
                        to="/management/database?from=0"
                      >
                        <span class="text">データベース</span>
                      </BaseLink>
                    </BaseDropdownItem>
                  </li>
                  <li>
                    <BaseDropdownItem>
                      <BaseLink
                        class="jps-header-dropdown-link"
                        to="/management/gallery?from=0&sort=edit.date:desc"
                      >
                        <span class="text">ギャラリー</span>
                      </BaseLink>
                    </BaseDropdownItem>
                  </li>
                  <li>
                    <BaseDropdownItem>
                      <BaseLink
                        class="jps-header-dropdown-link"
                        to="/management/csearch?from=0&sort=edit.date:desc"
                      >
                        <span class="text">テーマ別検索</span>
                      </BaseLink>
                    </BaseDropdownItem>
                  </li>
                </ul>
              </li>

              <li v-else>
                <BaseDropdownItem>
                  <BaseLink
                    class="jps-header-dropdown-link"
                    to="/management/participation"
                  >
                    <span class="text">機関登録</span>
                  </BaseLink>
                </BaseDropdownItem>
              </li>

              <li>
                <ul>
                  <li>
                    <BaseDropdownItem>
                      <BaseLink
                        class="jps-header-dropdown-link"
                        to="/management/account"
                      >
                        <span class="text">アカウント設定</span>
                      </BaseLink>
                    </BaseDropdownItem>
                  </li>
                  <li>
                    <BaseDropdownItem>
                      <button
                        class="jps-header-dropdown-link"
                        @click="changeOrg()"
                      >
                        <span class="text">機関切替</span>
                      </button>
                    </BaseDropdownItem>
                  </li>
                  <li>
                    <BaseDropdownItem>
                      <a
                        class="jps-header-dropdown-link"
                        @click="logout()"
                      >
                        <span class="text">ログアウト</span>
                      </a>
                    </BaseDropdownItem>
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </BaseDropdown>

        <!-- 言語 -->
        <BaseLink
          :to="switchLangUrl"
          :ignore-lang="true"
          class="jps-header-text-with-icon-link"
        >
          <BaseIcon name="mdi/web" />
          {{ !isJa ? '日本語' : 'English' }}
        </BaseLink>

        <!-- ノートメニュー -->
        <BaseDropdown
          v-if="noteStore.primary"
          class="jps-header-dropdown"
        >
          <template #trigger>
            <span
              class="jps-header-text-with-icon-link jps-header-button-mynote"
            >
              <BaseIcon
                class="jps-header-button-mynote-icon"
                name="mdi/notebook"
                alt="マイノート"
              />
              <span class="jps-header-button-mynote-text">
                <span>{{ lss("マイノート", "My Note") }}</span>
                <BaseIcon
                  class="jps-header-button-mynote-chevron"
                  name="mdi/menu-down"
                  size="size-full"
                />
              </span>
            </span>
          </template>

          <div
            class="jps-header-dropdown-body jps-header-dropdown-note"
          >
            <span
              v-if="noteStore.list.length > 1"
              class="dropdown-body-supplement"
            >
              {{ lss("メインのノートを切り替え", "Select defalut note") }}
            </span>

            <ul
              class="jps-header-dropdown-list"
            >
              <li>
                <ul>
                  <li
                    v-for="(n, index) in noteList"
                    :key="n.id"
                  >
                    <BaseDropdownItem
                      :class="{ 'is-selected': index === 0 }"
                    >
                      <BaseLink
                        v-if="index == 0"
                        to="/mynote"
                        class="jps-header-dropdown-link"
                      >
                        <BaseIcon
                          name="mdi/notebook"
                          class="icon"
                          size="size-5"
                        />
                        <span class="text jps-header-text-truncate">{{ ls(n.title) }}</span>

                        <span class="jps-header-dropdown-list-selected-tag jps-tag">
                          {{ lss("選択中", "Selected") }}
                        </span>
                      </BaseLink>

                      <button
                        v-else
                        @click="noteStore.changePrimary(n.id)"
                        class="jps-header-dropdown-link"
                      >
                        <BaseIcon
                          name="mdi/notebook"
                          size="size-5"
                          class="icon"
                        />
                        <span class="text jps-header-text-truncate">{{ ls(n.title) }}</span>
                      </button>
                    </BaseDropdownItem>
                  </li>
                </ul>
              </li>

              <li>
                <BaseDropdownItem
                  class="jps-header-dropdown-create-new"
                >
                  <div>
                    <button
                      @click="createNewNote()"
                      class="jps-header-dropdown-link"
                    >
                      <BaseIcon
                        name="mdi/notebook"
                        size="size-5"
                        class="icon"
                      />
                      <span class="text">{{ lss("新しいノートを作る", "Create new note") }}</span>
                    </button>
                  </div>
                  <div>
                    <BaseLink to="/mynote" class="jps-header-dropdown-link">
                      <span class="text">{{ lss("一覧", "List") }}</span>
                    </BaseLink>
                  </div>
                </BaseDropdownItem>
              </li>
              <li>
                <BaseDropdownItem
                  class="jps-header-dropdown-mypages"
                >
                  <div>
                    <BaseLink to="/mygallery" class="jps-header-dropdown-link is-column">
                      <BaseIcon
                        name="mdi/image-album"
                        class="icon"
                      />
                      <span class="text">{{ lss("マイギャラリー", "My Gallery") }}</span>
                    </BaseLink>
                  </div>
                  <div>
                    <BaseLink to="/mysearch" class="jps-header-dropdown-link is-column">
                      <BaseIcon
                        name="mdi/account-search"
                        size="size-4"
                        class="icon"
                      />
                      <span class="text">{{ lss("マイサーチ", "My Search") }}</span>
                    </BaseLink>
                  </div>
                </BaseDropdownItem>
              </li>
            </ul>
          </div>
        </BaseDropdown>

      </nav>
    </div>
  </header>

  <SwitchAffilitionModal ref="switchAffilitionModal" /><!-- アカウントの切り替えモーダル -->
  <UiInputModal ref="inputModal" :title="{ ja: '新しいノートのタイトル', en: 'Title for new note' }" />
</template>

<script lang="ts" setup>
import type { UiInputModal, SwitchAffilitionModal } from '#components'

const { ls, lss, led, switchLangUrl, isJa, langPath } = useI18n()
const router = useRouter()
const userStore = useUserStore()
const noteStore = useNoteStore()
const { snackbarSuccess } = useSnackbar()
const { signout } = useUserStore()

// 組織系
const userRoleDef = USER_ROLE_DEF
const switchAffilitionModal = ref<InstanceType<typeof SwitchAffilitionModal>>()
const changeOrg = async () => {
  try {
    await switchAffilitionModal.value.openAwait()
  } catch (e) {}
}

// サインアウト
const logout = async () => {
  const result = await signout()
  if (result) {
    router.push(langPath('/'))
  }
}

// ノート系
const inputModal = ref<InstanceType<typeof UiInputModal>>()
const createNewNote = async () => {
  try {
    const title = await inputModal.value.openAwait()
    if (!title) return
    let note = await noteStore.createNewNoteByTitle({ ja: title })
    await noteStore.changePrimary(note.id)
    snackbarSuccess(
      {
        ja: title + 'を作成しました',
        en: title + ' is created',
      }
    )
  } catch (cancel) {}
}

const noteList = computed(() => {
  return noteStore.list.slice(0, 5)
})

const checkScrollPosition = () => {
  scrollPosition.value = window.scrollY
}
const route = useRoute()
const isHome = computed(() => route.path === '/')

const scrollPosition = ref(0)
const hasShadow = computed(() => {
  if (isHome.value) {
    return scrollPosition.value > 50
  } else {
    return true
  }
})

onMounted(() => {
  checkScrollPosition()

  if (isHome.value) {
    document.addEventListener('scroll', checkScrollPosition)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('scroll', checkScrollPosition)
})

watch(() => route.path, () => {
  checkScrollPosition()

  if (isHome.value) {
    document.addEventListener('scroll', checkScrollPosition)
  } else {
    document.removeEventListener('scroll', checkScrollPosition)
  }
})
</script>

<style lang="scss" scoped>
@use 'sass:map';
@use '@/assets/styles/scss/vue' as *;

.jps-header {
  background-color: $white;
  padding-left: 1rem;
  padding-right: 1rem;
  @include transition-all();

  @include is-tablet {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }

  &.jps-header-fixed {
    width: 100%;
    height: $header-height-mobile;
    top: 0;
    position: fixed;
    z-index: 2000;

    @include is-tablet() {
      height: $header-height-tablet;
    }
  }

  &.has-shadow {
    box-shadow: 0 0 0.5rem rgba(0, 0, 0, 0.1);
  }

  .jps-header-inner {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .jps-header-text-with-icon-link {
    min-height: 2.75rem;
    font-size: $size-7;
    column-gap: 0.25rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: $jps-gray-900;

    &:hover, &:focus {
      text-decoration: underline;
    }

    @include is-mobile {
      font-size: $size-8;
      flex-direction: column;
    }
  }

  .jps-header-button-mynote {
    .jps-header-button-mynote-icon {
      @include is-mobile {
        margin-left: -12px;
      }
    }

    .jps-header-button-mynote-text {
      display: flex;
      align-items: center;
    }

    .jps-header-button-mynote-chevron {
      max-width: 24px;
      max-height: 24px;
      @include is-mobile {
        max-height: 20px;
        max-width: 20px;
      }
    }
  }

  .jps-header-text-truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .jps-header-management-item {
    @include is-mobile() {
      display: none !important;
    }
  }
}

.jps-header-main {
  display: flex;
  align-items: center;
  gap: 0.75rem;

  .jps-header-main-logo {
    @include is-mobile {
      max-width: 2rem;
      overflow: hidden;
    }
  }

  .jps-header-main-logo-svg {
    width: 12rem;
    height: 2rem;
    display: block;
  }

  .jps-header-main-nav {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
}

.jps-header-nav {
  pointer-events: auto;
  flex-shrink: 0;
  align-items: stretch;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;

  .jps-header-dropdown {
    width: max-content;
  }

  .jps-header-dropdown-body {
    max-width: 90vw;

    @include is-tablet() {
      max-width: 16rem;
    }

    .dropdown-body-supplement {
      font-size: $size-8;
      padding: 0.825rem 1rem 0.25rem;
      display: flex;
      align-items: center;

      @include is-mobile {
        font-weight: bold;
      }
    }
  }
  .jps-header-dropdown-note {
    width: 16rem;
  }

  .jps-header-dropdown-list {
    li > .base-dropdown-item {  // BaseDropdownItem のスタイルを上書き
      padding: 0;

      &:hover, &:focus {
        background-color: transparent;
      }
    }

    > li + li {
      border-top: 1px solid $jps-gray-300;
    }

    .jps-header-dropdown-list-selected-tag {
      background: $jps-gray-200;
      flex-shrink: 0;
      max-height: 1.5rem;
      font-size: $size-8;
    }
  }

  .jps-header-dropdown-link {
    width: 100%;
    min-height: 2.5rem;
    display: flex;
    align-items: center;
    gap: 0.25rem 0.25rem;
    padding: 0.375rem 1rem;
    font-size: $size-8;
    flex-grow: 1;
    color: $jps-key-blue;
    cursor: pointer;

    &.is-column {
      justify-content: center;
      flex-direction: column;
    }

    @include is-mobile {
      font-weight: bold;
    }

    &:hover, &:focus {
      background-color: $jps-gray-100;

      .text {
        color: $jps-key-blue-hover;
        text-decoration: underline;
      }

      .icon {
        color: $jps-key-blue-hover;
      }
    }

    &:visited {
      .text {
        color: $jps-key-blue-hover;
      }

      .icon {
        color: $jps-key-blue-hover;
      }
    }

    .icon {
      color: $jps-key-blue;
      flex-shrink: 0;
    }

    .text {
      display: block;
    }
  }

  .jps-header-dropdown-create-new {
    width: 100%;
    display: flex;

    > *:first-child {
      flex-grow: 1;
    }
  }

  .jps-header-dropdown-mypages {
    width: 100%;
    display: flex;

    > * {
      flex: 1 0 7.25rem;
      min-width: 7.25rem;
    }
  }
}
</style>